import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ResourceCard from "../components/ResourceCard";
import StatCounter from "../components/StatCounter";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { publicService } from "../services/publicService";
import {
	getSelectedResourceIds,
	upsertSelectedResource,
	removeSelectedResource,
	INQUIRY_SELECTION_EVENT,
} from "../utils/inquirySelection";

export default function Home() {
	const [resources, setResources] = useState([]);
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState({
		technical_experts: 0,
		categories: 0,
		service_tier: 0,
	});
	const [selectedResourceIds, setSelectedResourceIds] = useState([]);
	const [commentModal, setCommentModal] = useState({
		open: false,
		resourceId: null,
		resourceName: "",
		comment: "",
	});

	useEffect(() => {
		const syncSelected = () => {
			setSelectedResourceIds(getSelectedResourceIds());
		};

		syncSelected();
		window.addEventListener(INQUIRY_SELECTION_EVENT, syncSelected);

		return () => {
			window.removeEventListener(INQUIRY_SELECTION_EVENT, syncSelected);
		};
	}, []);

	useEffect(() => {
		setLoading(true);
		Promise.all([
			publicService.getFeaturedResources().then(setResources),
			publicService
				.getHomeStats()
				.then((data) => {
					setStats({
						technical_experts: data.technical_experts || 0,
						categories: data.categories || 0,
						service_tier: data.service_tier || 0,
					});
				})
				.catch(() => {
					setStats((prev) => ({
						...prev,
						technical_experts: resources.length || 0,
					}));
				}),
		]).finally(() => setLoading(false));
	}, []);

	const featured = resources.slice(0, 3);

	const handleToggleSelect = (resourceId) => {
		const next = removeSelectedResource(resourceId).map((item) => item.id);
		setSelectedResourceIds(next);
	};

	const openCommentModal = (resourceId, resource) => {
		const resourceName =
			`${resource?.first_name || ""} ${resource?.last_name || ""}`.trim() ||
			resource?.role ||
			`Resource ID-${resourceId}`;
		setCommentModal({
			open: true,
			resourceId,
			resourceName,
			comment: "",
		});
	};

	const closeCommentModal = () => {
		setCommentModal({
			open: false,
			resourceId: null,
			resourceName: "",
			comment: "",
		});
	};

	const confirmSelectionWithComment = () => {
		const comment = commentModal.comment.trim();
		if (!comment) {
			return;
		}

		const next = upsertSelectedResource(commentModal.resourceId, comment).map(
			(item) => item.id,
		);
		setSelectedResourceIds(next);
		closeCommentModal();
	};

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<Navbar />

			<main className="flex-1 overflow-hidden">
				<section className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white overflow-hidden">
					<div className="absolute inset-0 opacity-10">
						<div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
						<div className="absolute bottom-10 right-10 w-96 h-96 bg-accent-400 rounded-full blur-3xl"></div>
					</div>
					<div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
						<div className="max-w-3xl">
							<div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm font-medium mb-6 backdrop-blur-sm border border-white/20">
								<span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
								Resources Available Now
							</div>
							<h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
								Find the Right Tech
								<span className="text-accent-400"> Expert</span>
								<br />
								for Your Project
							</h1>
							<p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-2xl leading-relaxed">
								Smart Resource Hub connects you with pre-vetted technical
								consultants and architects. Browse skills, review profiles, and
								staff your team instantly.
							</p>
							<div className="flex flex-wrap gap-4">
								<Link
									to="/resources"
									className="bg-white text-primary-700 hover:bg-blue-50 font-bold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
								>
									Browse Resources
								</Link>
								<Link
									to="/skill-matrix"
									className="border-2 border-white/50 text-white hover:bg-white/10 font-semibold py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center gap-2"
								>
									View Skill Matrix
								</Link>
							</div>
						</div>
					</div>
				</section>

				<section className="bg-white py-16 border-b border-gray-100">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
							<StatCounter
								value={stats.technical_experts}
								label="Technical Experts"
								suffix="+"
							/>
							<StatCounter value={stats.categories} label="Categories" />
							<StatCounter value={stats.service_tier} label="Service Tiers" />
						</div>
					</div>
				</section>

				{featured.length > 0 && (
					<section className="py-16 bg-gray-50">
						<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
							<div className="text-center mb-10">
								<h2 className="section-title">Featured Resources</h2>
								<p className="section-subtitle">
									Top talent available for immediate engagement
								</p>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
								{featured.map((r) => (
									<ResourceCard
										key={r.id}
										resource={r}
										selectable
										selected={selectedResourceIds.includes(r.id)}
										onToggleSelect={handleToggleSelect}
										onRequestSelect={openCommentModal}
									/>
								))}
							</div>
							<div className="text-center">
								<Link to="/resources" className="btn-primary">
									View All Resources
								</Link>
							</div>
						</div>
					</section>
				)}
			</main>

			<Footer />
			{commentModal.open && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
					<div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Add Comment for Inquiry
						</h3>
						<p className="text-sm text-gray-600 mb-4">
							{commentModal.resourceName}
						</p>
						<textarea
							value={commentModal.comment}
							onChange={(e) =>
								setCommentModal((prev) => ({
									...prev,
									comment: e.target.value,
								}))
							}
							rows={4}
							className="input-field resize-none"
							placeholder="Enter mandatory comment for this resource"
						/>
						<div className="mt-5 flex justify-end gap-2">
							<button
								type="button"
								onClick={closeCommentModal}
								className="btn-secondary"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={confirmSelectionWithComment}
								disabled={!commentModal.comment.trim()}
								className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
